import {
  Modal,
  Text,
  Box,
  Stack,
  Group,
  Title,
  Divider,
  Button,
} from '@mantine/core';
import { useIntl } from 'react-intl';
import { QueueStats } from '../../../helpers/queueManagementDropdown';

interface QueueStatsModalProps {
  opened: boolean;
  onClose: () => void;
  stats: QueueStats | null;
}

export const QueueStatsModal = ({
  opened,
  onClose,
  stats,
}: QueueStatsModalProps) => {
  const intl = useIntl();

  if (!stats) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({ id: 'queue.detailedStatistics' })}
      size="xl"
    >
      <Stack gap="lg">
        {/* Overview Section */}
        {stats.overview && (
          <Box>
            <Title order={4} mb="sm">
              📊 {intl.formatMessage({ id: 'queue.overview' })}
            </Title>
            <Stack gap="xs">
              <Group justify="apart">
                <Text fw={500}>
                  {intl.formatMessage({ id: 'queue.status' })}:
                </Text>
                <Text
                  c={stats.overview.status === 'healthy' ? 'green' : 'orange'}
                >
                  {stats.overview.status === 'healthy'
                    ? '✅ ' + intl.formatMessage({ id: 'queue.healthy' })
                    : '⚠️ ' + intl.formatMessage({ id: 'queue.degraded' })}
                </Text>
              </Group>
              <Group justify="apart">
                <Text fw={500}>
                  {intl.formatMessage({ id: 'queue.pendingJobs' })}:
                </Text>
                <Text>{stats.overview.pendingJobs}</Text>
              </Group>
              <Group justify="apart">
                <Text fw={500}>
                  {intl.formatMessage({ id: 'queue.activeConsumers' })}:
                </Text>
                <Text>{stats.overview.activeConsumers}</Text>
              </Group>
              <Group justify="apart">
                <Text fw={500}>
                  {intl.formatMessage({ id: 'queue.processingRate' })}:
                </Text>
                <Text>{stats.overview.processingRate}</Text>
              </Group>
              {stats.overview.issues && stats.overview.issues.length > 0 && (
                <Box>
                  <Text fw={500} c="orange" mb={4}>
                    {intl.formatMessage({ id: 'queue.issues' })}:
                  </Text>
                  {stats.overview.issues.map((issue, idx) => (
                    <Text key={idx} size="sm" c="orange" pl="md">
                      • {issue}
                    </Text>
                  ))}
                </Box>
              )}
            </Stack>
          </Box>
        )}

        <Divider />

        {/* Health Section */}
        {stats.health && (
          <Box>
            <Title order={4} mb="sm">
              💚 {intl.formatMessage({ id: 'queue.healthStatus' })}
            </Title>
            <Stack gap="xs">
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.initialized' })}:</Text>
                <Text>{stats.health.initialized ? '✅' : '❌'}</Text>
              </Group>
              <Group justify="apart">
                <Text fw={600}>
                  {intl.formatMessage({ id: 'queue.rabbitmqConnected' })}:
                </Text>
                <Text
                  fw={600}
                  c={stats.health.rabbitmqConnected ? 'green' : 'red'}
                >
                  {stats.health.rabbitmqConnected
                    ? '✅ ' + intl.formatMessage({ id: 'queue.healthy' })
                    : '❌ ' + intl.formatMessage({ id: 'queue.disconnected' })}
                </Text>
              </Group>
              <Group justify="apart">
                <Text size="sm" c="dimmed">
                  {intl.formatMessage({ id: 'queue.rabbitmqInitialized' })}:
                </Text>
                <Text size="sm" c="dimmed">
                  {stats.health.rabbitmqInitialized ? '✅' : '❌'}
                </Text>
              </Group>
              <Group justify="apart">
                <Text size="sm" c="dimmed">
                  {intl.formatMessage({ id: 'queue.connectionState' })}:
                </Text>
                <Text size="sm" c="dimmed">
                  {stats.health.rabbitmqConnectionState}
                </Text>
              </Group>
              <Group justify="apart">
                <Text size="sm" c="dimmed">
                  {intl.formatMessage({ id: 'queue.channelState' })}:
                </Text>
                <Text size="sm" c="dimmed">
                  {stats.health.channelState}
                </Text>
              </Group>
              <Group justify="apart">
                <Text>
                  {intl.formatMessage({ id: 'queue.consumerRunning' })}:
                </Text>
                <Text>{stats.health.consumerRunning ? '✅' : '❌'}</Text>
              </Group>
              {stats.health.healthCheck && (
                <Group justify="apart">
                  <Text>
                    {intl.formatMessage({ id: 'queue.healthCheckFailures' })}:
                  </Text>
                  <Text
                    c={
                      stats.health.healthCheck.healthCheckFailureCount > 0
                        ? 'orange'
                        : undefined
                    }
                  >
                    {stats.health.healthCheck.healthCheckFailureCount}
                  </Text>
                </Group>
              )}
            </Stack>
          </Box>
        )}

        <Divider />

        {/* Metrics Section */}
        {stats.metrics && (
          <Box>
            <Title order={4} mb="sm">
              📈 {intl.formatMessage({ id: 'queue.metrics' })}
            </Title>
            <Stack gap="xs">
              <Group justify="apart">
                <Text fw={600} c="blue">
                  {intl.formatMessage({ id: 'queue.totalProcessed' })}:
                </Text>
                <Text fw={600} c="blue">
                  {stats.metrics.totalProcessed.toLocaleString()}
                </Text>
              </Group>
              <Group justify="apart">
                <Text>
                  {intl.formatMessage({ id: 'queue.totalPublished' })}:
                </Text>
                <Text>{stats.metrics.totalPublished.toLocaleString()}</Text>
              </Group>
              <Group justify="apart">
                <Text>
                  {intl.formatMessage({ id: 'queue.totalDelivered' })}:
                </Text>
                <Text>{stats.metrics.totalDelivered.toLocaleString()}</Text>
              </Group>
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.pendingJobs' })}:</Text>
                <Text>{stats.metrics.pendingJobs}</Text>
              </Group>
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.readyJobs' })}:</Text>
                <Text>{stats.metrics.readyJobs}</Text>
              </Group>
              <Group justify="apart">
                <Text>
                  {intl.formatMessage({ id: 'queue.unacknowledgedJobs' })}:
                </Text>
                <Text>{stats.metrics.unacknowledgedJobs}</Text>
              </Group>
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.consumers' })}:</Text>
                <Text>{stats.metrics.consumers}</Text>
              </Group>
              <Group justify="apart">
                <Text>
                  {intl.formatMessage({ id: 'queue.consumerUtilization' })}:
                </Text>
                <Text>
                  {(stats.metrics.consumerUtilization * 100).toFixed(1)}%
                </Text>
              </Group>
              {stats.metrics.messageRate && (
                <>
                  <Group justify="apart">
                    <Text>
                      {intl.formatMessage({ id: 'queue.publishRate' })}:
                    </Text>
                    <Text>
                      {stats.metrics.messageRate.publish.toFixed(2)}/s
                    </Text>
                  </Group>
                  <Group justify="apart">
                    <Text>
                      {intl.formatMessage({ id: 'queue.deliverRate' })}:
                    </Text>
                    <Text>
                      {stats.metrics.messageRate.deliver.toFixed(2)}/s
                    </Text>
                  </Group>
                  <Group justify="apart">
                    <Text>{intl.formatMessage({ id: 'queue.ackRate' })}:</Text>
                    <Text>{stats.metrics.messageRate.ack.toFixed(2)}/s</Text>
                  </Group>
                </>
              )}
            </Stack>
          </Box>
        )}

        <Divider />

        {/* System Section */}
        {stats.system && (
          <Box>
            <Title order={4} mb="sm">
              💻 {intl.formatMessage({ id: 'queue.system' })}
            </Title>
            <Stack gap="xs">
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.nodeVersion' })}:</Text>
                <Text>{stats.system.nodeVersion}</Text>
              </Group>
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.uptime' })}:</Text>
                <Text>{stats.system.uptime}s</Text>
              </Group>
              {stats.system.memoryUsage && (
                <Group justify="apart">
                  <Text>
                    {intl.formatMessage({ id: 'queue.memoryUsage' })}:
                  </Text>
                  <Text>
                    {stats.system.memoryUsage.used} /{' '}
                    {stats.system.memoryUsage.total}{' '}
                    {stats.system.memoryUsage.unit}
                  </Text>
                </Group>
              )}
              <Group justify="apart">
                <Text>{intl.formatMessage({ id: 'queue.timestamp' })}:</Text>
                <Text size="xs">
                  {new Date(stats.system.timestamp).toLocaleString()}
                </Text>
              </Group>
            </Stack>
          </Box>
        )}

        <Divider />

        {/* Architecture Section */}
        {stats.architecture && (
          <Box>
            <Title order={4} mb="sm">
              🏗️ {intl.formatMessage({ id: 'queue.architecture' })}
            </Title>
            <Stack gap="xs">
              <Text fw={500}>{stats.architecture.mode}</Text>
              <Text size="sm" c="dimmed">
                {stats.architecture.tracking}
              </Text>
              {stats.architecture.components &&
                stats.architecture.components.length > 0 && (
                  <Box>
                    <Text fw={500} size="sm" mb={4}>
                      {intl.formatMessage({ id: 'queue.components' })}:
                    </Text>
                    {stats.architecture.components.map((component, idx) => (
                      <Text key={idx} size="xs" c="dimmed" pl="md">
                        • {component}
                      </Text>
                    ))}
                  </Box>
                )}
            </Stack>
          </Box>
        )}

        <Group justify="flex-end" mt="md">
          <Button onClick={onClose}>
            {intl.formatMessage({ id: 'close' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
